import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { notify } from '@speeki/global-ui-components';

import {
    CheckPasswordResetTokenDocument,
    TenantLogoDocument,
} from '@graphql/generated/graphql';

import { useFindCompanyByCode } from '@utils/hooks/useFindCompanyByCode';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useMutation } from '@apollo/client';
import { ERROR_MESSAGES } from '@shared/notifyMessages';

/**
 * we receive below URL to email after reset password request and cut 5 last charts to get code only
 * /auth/password-reset/confirm?code=487ca039a3a87fd5ebfd47f47fc76730__VZAL4
 */
const REMOVED_CHARTS = -5;

export const useCheckResetToken = (code: string) => {
    const companyCode = code.slice(REMOVED_CHARTS);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [findCompanyByCode, companyOptions] = useFindCompanyByCode();
    const [checkPasswordResetToken, resetOptions] = useMutation(
        CheckPasswordResetTokenDocument,
        {
            context: {
                notifyOnError: true,
            },
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => {
                if (!data.success) {
                    notify(t(ERROR_MESSAGES.auth.tokenExpired)).ERROR();
                    navigate(getPath({ path: '/auth/welcome' }));
                }
            },
            onError: () => {},
        },
    );

    const [getTenantLogo, { data: tenantData }] =
        useLazyQuery(TenantLogoDocument);

    const loading =
        companyOptions.loading ||
        !companyOptions.called ||
        resetOptions.loading ||
        !resetOptions.called;

    useEffect(() => {
        const checkCode = async () => {
            try {
                const company = await findCompanyByCode({
                    variables: {
                        companyCode,
                    },
                });
                await getTenantLogo({
                    variables: {
                        uuid: company.data?.tenant.uuid || '',
                    },
                });
                await checkPasswordResetToken({
                    variables: {
                        input: {
                            token: code,
                        },
                    },
                });
            } catch (error) {
                navigate(getPath({ path: '/auth/login' }));
                console.error('useCheckResetToken', error);
            }
        };

        void checkCode();
    }, []);

    return {
        loading,
        tenantLogo: tenantData?.tenant.logo,
    };
};
